<template>
    <AmModal
        :open="open"
        size="narrow"
        title="Мои ссылки"
        subtitle="Share-ссылки, которые вы создавали из своих диалогов."
        @close="onClose"
    >
        <div v-if="loading" class="state">Загружаю…</div>

        <div v-else-if="items.length === 0" class="state">
            Пока пусто. Создайте ссылку из меню «…» внутри диалога.
        </div>

        <ul v-else class="shares-list">
            <li
                v-for="s in items"
                :key="s.id"
                class="share-row"
                :class="{ 'share-row--revoked': !s.isActive || s.revokedAt }"
            >
                <div class="share-main">
                    <div class="share-title">
                        {{ s.title }}
                    </div>
                    <div class="share-meta">
                        <span class="meta-badge" :class="{ 'meta-badge--off': !s.isActive }">
                            {{ s.isActive && !s.revokedAt ? statusLabel(s) : 'отозвана' }}
                        </span>
                        <span>{{ modeLabel(s.mode) }}</span>
                        <span v-if="s.expiresAt">· истекает {{ formatDate(s.expiresAt) }}</span>
                        <span v-else>· без срока</span>
                        <span>· {{ s.viewCount }} {{ viewWord(s.viewCount) }}</span>
                    </div>
                </div>
                <div class="share-actions">
                    <button
                        type="button"
                        class="icon-btn"
                        title="Скопировать ссылку"
                        @click="copyUrl(s)"
                    >
                        <ChatIcon name="copy" :size="13" />
                    </button>
                    <button
                        type="button"
                        class="icon-btn"
                        title="Открыть"
                        @click="openShare(s)"
                    >
                        <ChatIcon name="link" :size="13" />
                    </button>
                    <button
                        v-if="s.isActive && !s.revokedAt"
                        type="button"
                        class="icon-btn icon-btn--danger"
                        title="Отозвать"
                        @click="onRevoke(s)"
                    >
                        <ChatIcon name="trash" :size="13" />
                    </button>
                </div>
            </li>
        </ul>
    </AmModal>
</template>

<script setup>
import { ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { getMyShares, revokeShare } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const loading = ref(false)
const items = ref([])

watch(
    () => props.open,
    async (isOpen) => {
        if (!isOpen) return
        await load()
    },
)

async function load() {
    loading.value = true
    try {
        items.value = await getMyShares()
    } catch {
        items.value = []
    } finally {
        loading.value = false
    }
}

function formatDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function modeLabel(mode) {
    return mode === 'forkable' ? 'можно продолжить' : 'только просмотр'
}

function statusLabel(s) {
    if (s.expiresAt && new Date(s.expiresAt) < new Date()) return 'истекла'
    return 'активна'
}

function viewWord(n) {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return 'просмотр'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'просмотра'
    return 'просмотров'
}

function urlFor(s) {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/chat/share/${s.token}`
}

function copyUrl(s) {
    try { navigator.clipboard.writeText(urlFor(s)) } catch (e) { /* ignore */ }
}

function openShare(s) {
    if (typeof window === 'undefined') return
    window.open(urlFor(s), '_blank', 'noopener')
}

async function onRevoke(s) {
    if (!confirm('Отозвать ссылку? Получатели больше не смогут её открыть.')) return
    try {
        await revokeShare(s.id)
        await load()
    } catch (e) {
        // ignore
    }
}

function onClose() { emit('close') }
</script>

<style scoped>
.state {
    padding: 28px 4px;
    text-align: center;
    color: #888780;
    font-size: 13px;
}

.shares-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
}

.share-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 10px 4px;
    border-radius: 8px;
}

.share-row:hover {
    background: #FAFAF7;
}

.share-row--revoked .share-title {
    color: #888780;
    text-decoration: line-through;
}

.share-main {
    min-width: 0;
}

.share-title {
    font-size: 13px;
    font-weight: 600;
    color: #23282C;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.share-meta {
    font-size: 11px;
    color: #888780;
    margin-top: 2px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.meta-badge {
    background: #ECF7E6;
    color: #1A6A2D;
    padding: 1px 6px;
    border-radius: 3px;
    font-weight: 600;
    font-size: 10px;
    text-transform: uppercase;
}

.meta-badge--off {
    background: #F1EFE8;
    color: #888780;
}

.share-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

.icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #E8E6E0;
    background: white;
    color: #23282C;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.icon-btn:hover {
    border-color: #23282C;
}

.icon-btn--danger:hover {
    color: #C0050F;
    border-color: #C0050F;
}
</style>
